import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import {
  dailyTrend,
  supportsCitations,
  workflowLabel,
  type TelemetryApplication,
  type RunSummary,
  type CitationSummary
} from "../../shared/telemetry";
import { api, type Session } from "./api";
import { demoApplications, demoRuns, demoCitations } from "./telemetry-demo";
import { DailyTrend, fmtUtcDay } from "./TelemetryTrend";
import { Empty, fmtInt, Notice, PageHead, PanelLoading, Segmented } from "./ui";

const ranges = [7, 30, 90].map((days) => ({ value: String(days), label: `${days} days` }));
const failure = "Telemetry could not be loaded. Please try again.";

export function TelemetryView({ session, demo }: { session: Session; demo: boolean }) {
  const [days, setDays] = useState("30");
  const [application, setApplication] = useState("");
  const [operation, setOperation] = useState("");
  const [workflow, setWorkflow] = useState("");
  const [applications, setApplications] = useState<TelemetryApplication[]>();
  const [registryError, setRegistryError] = useState("");
  const [runs, setRuns] = useState<RunSummary>();
  const [citations, setCitations] = useState<CitationSummary>();
  const [runError, setRunError] = useState("");
  const [citationError, setCitationError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const selectedApps =
    applications?.filter((app) => !application || app.application === application) ?? [];
  const operations = [...new Set(selectedApps.flatMap((app) => app.operations.map((op) => op.id)))];
  const workflows = [
    ...new Set(
      selectedApps.flatMap((app) =>
        app.operations
          .filter((op) => !operation || op.id === operation)
          .flatMap((op) => op.workflows)
      )
    )
  ];
  const hasCitations = supportsCitations(applications ?? [], {
    days: Number(days),
    ...(application ? { application } : {}),
    ...(operation ? { operation } : {}),
    ...(workflow ? { workflow } : {})
  });
  const appName = (id: string) =>
    applications?.find((app) => app.application === id)?.display_name ?? id;
  const retry = () => setAttempt((value) => value + 1);

  useEffect(() => {
    const abort = new AbortController();
    setRegistryError("");
    void (
      demo
        ? Promise.resolve({ applications: demoApplications })
        : api<{ applications: TelemetryApplication[] }>("/api/telemetry/applications", session, {
            signal: abort.signal
          })
    )
      .then((result) => {
        if (!abort.signal.aborted) setApplications(result.applications);
      })
      .catch(() => {
        if (!abort.signal.aborted) {
          setApplications(undefined);
          setRegistryError(failure);
        }
      });
    return () => abort.abort();
  }, [demo, session, attempt]);

  useEffect(() => {
    const abort = new AbortController();
    setRuns(undefined);
    setCitations(undefined);
    setRunError("");
    setCitationError("");
    if (!applications) return () => abort.abort();
    const query = new URLSearchParams({ days });
    if (application) query.set("application", application);
    if (workflow) query.set("workflow", workflow);
    const citationQuery = query.toString();
    if (operation) query.set("operation", operation);
    void (
      demo
        ? Promise.resolve(demoRuns(Number(days), application))
        : api<RunSummary>(`/api/telemetry/summary?${query}`, session, { signal: abort.signal })
    )
      .then((value) => {
        if (!abort.signal.aborted) setRuns(value);
      })
      .catch(() => {
        if (!abort.signal.aborted) setRunError(failure);
      });
    if (hasCitations)
      void (
        demo
          ? Promise.resolve(demoCitations(Number(days), application))
          : api<CitationSummary>(
              `/api/telemetry/citation-audits/summary?${citationQuery}`,
              session,
              { signal: abort.signal }
            )
      )
        .then((value) => {
          if (!abort.signal.aborted) setCitations(value);
        })
        .catch(() => {
          if (!abort.signal.aborted) setCitationError(failure);
        });
    return () => abort.abort();
  }, [days, application, operation, workflow, applications, hasCitations, demo, session, attempt]);

  return (
    <section className="view telemetry-view">
      <PageHead
        title="Telemetry"
        // The range end is a UTC date: show that date in every time zone.
        count={runs ? `${runs.range.days} days to ${fmtUtcDay(runs.range.end, true)}` : undefined}
        authority="Evidite telemetry · read only"
        authorityMode="read"
      />
      <div className="toolbar telemetry-filters">
        <Segmented label="Reporting range" value={days} options={ranges} onChange={setDays} />
        <label>
          Application
          <select
            aria-label="Application"
            value={application}
            disabled={!applications}
            onChange={(event) => {
              setApplication(event.target.value);
              setOperation("");
              setWorkflow("");
            }}
          >
            <option value="">All applications</option>
            {applications?.map((app) => (
              <option key={app.application} value={app.application}>
                {app.display_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Operation
          <select
            aria-label="Operation"
            value={operation}
            disabled={!applications}
            onChange={(event) => {
              setOperation(event.target.value);
              setWorkflow("");
            }}
          >
            <option value="">All operations</option>
            {operations.map((id) => (
              <option key={id} value={id}>
                {id.replaceAll(/[-_]/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Workflow
          <select
            aria-label="Workflow"
            value={workflow}
            disabled={!workflows.length}
            onChange={(event) => setWorkflow(event.target.value)}
          >
            <option value="">All workflows</option>
            {workflows.map((id) => (
              <option key={id} value={id}>
                {workflowLabel(id)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="scroll-page">
        <p className="foot-note">
          Consenting runs recorded by Evidite telemetry only—not all usage or billing activity.
          Legacy history is not included.
        </p>
        {registryError ? (
          <Retry message={registryError} onRetry={retry} />
        ) : !applications ? (
          <PanelLoading />
        ) : (
          <>
            <section className="block" aria-label="Run statistics">
              <div className="block-head">
                <h2>Run statistics</h2>
                <p>Success, failure and cancellation across the selected applications.</p>
              </div>
              {runError ? (
                <Retry message={runError} onRetry={retry} />
              ) : !runs ? (
                <PanelLoading />
              ) : !runs.overall ? (
                <Suppressed floor={runs.cohort_floor} />
              ) : (
                <>
                  <dl className="readouts">
                    <Metric label="Recorded runs" value={fmtInt(runs.overall.runs)} />
                    <Metric label="Succeeded" value={fmtInt(runs.overall.succeeded)} />
                    <Metric label="Failed" value={fmtInt(runs.overall.failed)} />
                    <Metric label="Cancelled" value={fmtInt(runs.overall.cancelled)} />
                    <Metric
                      label="Success rate"
                      value={`${(runs.overall.success_rate * 100).toFixed(1)}%`}
                    />
                    <Metric
                      label="Average duration"
                      value={`${Math.round(runs.overall.average_duration_seconds)}s`}
                    />
                  </dl>
                  <RunTable
                    label="By application"
                    rows={runs.by_application.map((row) => ({
                      ...row,
                      label: appName(row.application)
                    }))}
                  />
                  <RunTable
                    label="By workflow"
                    rows={runs.by_workflow.map((row) => ({
                      ...row,
                      label: `${appName(row.application)} / ${row.operation} / ${workflowLabel(row.workflow)}`
                    }))}
                  />
                  <DailyTrend
                    points={dailyTrend(runs.range, runs.daily)}
                    floor={runs.cohort_floor}
                  />
                  <RunTable
                    label="Daily runs (UTC)"
                    rows={runs.daily.map((row) => ({ ...row, label: row.date }))}
                  />
                  <p className="foot-note">
                    Groups below {runs.cohort_floor} runs are omitted. Visible groups may not sum to
                    the overall total.
                  </p>
                </>
              )}
            </section>
            {hasCitations && (
              <section className="block" aria-label="Citation results">
                <div className="block-head">
                  <h2>Citation results</h2>
                  <p>
                    Completed citation audits only. Type A is a subset verdict, not another outcome
                    bucket.
                  </p>
                </div>
                {citationError ? (
                  <Retry message={citationError} onRetry={retry} />
                ) : !citations ? (
                  <PanelLoading />
                ) : !citations.overall ? (
                  <Suppressed floor={citations.cohort_floor} />
                ) : (
                  <dl className="readouts">
                    <Metric label="Completed audits" value={fmtInt(citations.overall.runs)} />
                    <Metric label="Citations reviewed" value={fmtInt(citations.overall.total)} />
                    <Metric label="Type A verdicts" value={fmtInt(citations.overall.type_a)} />
                    <Metric
                      label="Type A rate"
                      value={
                        citations.overall.total
                          ? `${(citations.overall.type_a_rate * 100).toFixed(1)}%`
                          : "—"
                      }
                    />
                    {(["verified", "partial", "unverified", "skipped"] as const).map((key) => (
                      <Metric
                        key={key}
                        label={key[0]!.toUpperCase() + key.slice(1)}
                        value={fmtInt(citations.overall![key])}
                      />
                    ))}
                  </dl>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="readout">
      <dt>{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}
function Retry({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Notice tone="error">
      <span>{message}</span>{" "}
      <button className="btn btn-default" onClick={onRetry}>
        Retry telemetry
      </button>
    </Notice>
  );
}
function Suppressed({ floor }: { floor: number }) {
  return (
    <Empty icon={ShieldCheck} title="Not enough reportable data">
      This selection has fewer than {floor} recorded runs. Results are withheld rather than shown as
      zero.
    </Empty>
  );
}
function RunTable({
  label,
  rows
}: {
  label: string;
  rows: (NonNullable<RunSummary["overall"]> & { label: string })[];
}) {
  return (
    <div className="telemetry-table">
      <h3>{label}</h3>
      <table className="records">
        <thead>
          <tr>
            <th scope="col">{label === "Daily runs (UTC)" ? "Date" : "Group"}</th>
            {["Runs", "Succeeded", "Failed", "Cancelled"].map((name) => (
              <th key={name} scope="col" className="right">
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {(["runs", "succeeded", "failed", "cancelled"] as const).map((key) => (
                <td key={key} className="right num">
                  {fmtInt(row[key])}
                </td>
              ))}
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={5}>No groups reached the reporting threshold.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
